// Structural shape of the fields searchDocuments() reads. Kept narrow on purpose
// so it accepts both the full DocumentSummary from DocumentCard and the lighter
// local types used in global SearchResults.
export interface SearchableDocument {
  fileName?: string;
  documentType?: string;
  category?: string;
  summary?: string;
  tags?: string[];
  // Raw AI-extracted fields from listDocuments. We concatenate + lowercase
  // these here (in the browser) instead of on every backend read.
  extractedText?: string;
  extractedData?: Record<string, unknown>;
  // Reserved for Phase B (Vertex AI Search). When populated, takes precedence
  // over the per-field concatenation below.
  searchableText?: string;
}

// Recursively walk an extractedData JSON value and push string leaves into out.
// Folds entity names, providers, dates contexts, etc. into the searchable blob
// without coupling to the AI router's output shape.
function collectStringLeaves(value: unknown, out: string[]): void {
  if (value === null || value === undefined) return;
  if (typeof value === 'string') {
    if (value.length > 0) out.push(value);
    return;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    out.push(String(value));
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectStringLeaves(item, out);
    return;
  }
  if (typeof value === 'object') {
    for (const item of Object.values(value as Record<string, unknown>)) {
      collectStringLeaves(item, out);
    }
  }
}

function buildSearchableBlob(doc: SearchableDocument): string {
  const parts: string[] = [];
  if (doc.fileName) parts.push(doc.fileName);
  if (doc.documentType) parts.push(doc.documentType);
  if (doc.category) parts.push(doc.category);
  if (doc.summary) parts.push(doc.summary);
  if (doc.tags) {
    for (const tag of doc.tags) if (tag) parts.push(tag);
  }
  if (doc.extractedText) parts.push(doc.extractedText);
  if (doc.extractedData) collectStringLeaves(doc.extractedData, parts);
  return parts.join(' ').toLowerCase();
}

export function searchDocuments<T extends SearchableDocument>(
  documents: T[],
  query: string
): T[] {
  if (!query.trim()) return documents;

  const queryWords = query.toLowerCase().trim().split(/\s+/).filter((w) => w.length > 0);

  return documents.filter((doc) => {
    // Fast path: pre-computed searchableText from backend (Phase B).
    if (doc.searchableText) {
      return queryWords.every((word) => doc.searchableText!.includes(word));
    }

    // Phase A path: build the search blob from raw fields. Debounced upstream,
    // so this runs once per pause, not per keystroke.
    const blob = buildSearchableBlob(doc);
    return queryWords.every((word) => blob.includes(word));
  });
}

export interface HighlightSegment {
  text: string;
  highlighted: boolean;
}

export function highlightMatch(text: string, query: string): HighlightSegment[] {
  if (!query.trim() || !text) {
    return [{ text, highlighted: false }];
  }

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase().trim();
  const segments: HighlightSegment[] = [];
  let lastIndex = 0;

  let index = lowerText.indexOf(lowerQuery, lastIndex);
  while (index !== -1) {
    if (index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, index), highlighted: false });
    }
    segments.push({ text: text.slice(index, index + lowerQuery.length), highlighted: true });
    lastIndex = index + lowerQuery.length;
    index = lowerText.indexOf(lowerQuery, lastIndex);
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), highlighted: false });
  }

  return segments.length > 0 ? segments : [{ text, highlighted: false }];
}

// HTML-entity decoder for the small set of entities Discovery Engine emits in
// snippets. We don't pass user-controlled HTML to the DOM (React escapes it
// anyway), so we don't need a full HTML parser — just the entities DE produces.
const HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
};
function decodeEntities(s: string): string {
  return s.replace(/&(?:amp|lt|gt|quot|#39|apos|nbsp);/g, (m) => HTML_ENTITIES[m] || m);
}

/**
 * Parse a Discovery Engine snippet with <b>...</b> match markers into
 * HighlightSegment[]. Discovery Engine returns snippet strings already
 * containing the markup — using `highlightMatch()` on top of that would render
 * the raw tags as text (React escapes unknown HTML). This converts the
 * Discovery-Engine-provided highlighting into the same segment shape so the
 * existing `<mark>` renderer Just Works.
 *
 * Strips any other HTML tags defensively (Discovery Engine has been observed
 * occasionally including `<em>` for stemmed matches).
 */
export function parseHighlightedSnippet(snippet: string): HighlightSegment[] {
  if (!snippet) return [{ text: '', highlighted: false }];

  // Step 1: extract <b>...</b> ranges. Anything else inside is treated as text.
  const segments: HighlightSegment[] = [];
  const boldRe = /<b>([\s\S]*?)<\/b>/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = boldRe.exec(snippet)) !== null) {
    if (m.index > lastIndex) {
      segments.push({
        text: decodeEntities(snippet.slice(lastIndex, m.index).replace(/<[^>]+>/g, '')),
        highlighted: false,
      });
    }
    segments.push({
      text: decodeEntities(m[1].replace(/<[^>]+>/g, '')),
      highlighted: true,
    });
    lastIndex = boldRe.lastIndex;
  }

  if (lastIndex < snippet.length) {
    segments.push({
      text: decodeEntities(snippet.slice(lastIndex).replace(/<[^>]+>/g, '')),
      highlighted: false,
    });
  }

  // Drop empties produced by adjacent tags.
  const filtered = segments.filter((s) => s.text.length > 0);
  return filtered.length > 0 ? filtered : [{ text: '', highlighted: false }];
}
